<script lang="ts" context="module">
  import { type ObjectOption } from "svelte-multiselect";
  import type { SvelteComponent, ComponentType, ComponentProps } from "svelte";

  interface RenderComponent<T extends Record<string, any>, Row> {
    component: ComponentType<SvelteComponent<T>>;
    // if defined: component is provided with props provided.
    propsf?(this: Column<Row>, row: Row): ComponentProps<SvelteComponent<T>>;
    // if propfs not defined? props provided are any contained here, plus the current row at 'row'.
    props?: ComponentProps<SvelteComponent<T>>;
  }

  export interface Column<T> {
    width?: number;
    key: string;
    title: string;
    value?(row: T): string | number | null;
    sortable?: boolean;
    numeric?: boolean;
    // any entry (only one supported) in filterSelections for this col.key will be compared to
    // searchValue (for a substring match).
    search?: boolean;
    multiFilter?: boolean;
    filterOptions?: ObjectOption[] | ((this: Column<T>, rows: T[]) => ObjectOption[]);
    filterValue?(row: T): string;
    searchValue?(row: T): string;
    renderComponent?: RenderComponent<any, T>; // XXX this isn't enforcing that props match
    class?: string;
    headerClass?: string;
    addClass?: (row: T) => string;
    tooltip?: string;
  }
</script>

<script lang="ts">
  import { createEventDispatcher } from "svelte";

  import MultiSelect from "svelte-multiselect";

  type T = $$Generic<Record<string, any>>;

  export let columns: Column<T>[];

  export let rows: T[];

  type VisibleRow = T & { $sortOn: string | number };
  let visibleRows: VisibleRow[];

  export let sortOrders = [1, -1];

  export let hideWhenNoneVisible = false;

  // READ AND WRITE

  export let sortBy: string | number | undefined = "";

  export let sortOrder = sortOrders?.[0] || 1;

  export let filterSelections: Record<string, ObjectOption[]> = {};

  // READ ONLY

  export let iconAsc = "<img src='static/assets/icons/sort-asc.svg'>";

  export let iconDesc = "<img src='static/assets/icons/sort-desc.svg'>";

  export let iconSortable = "";

  export let classNameTable = "";

  export let classNameThead = "";

  export let classNameTbody = "";

  export let classNameSelect = "";

  export let classNameRow = "";

  export let classNameCell = "";

  // derive a key for each row, to help svelte out
  // https://learn.svelte.dev/tutorial/keyed-each-blocks
  export let key: (row: T) => unknown = (row: T) => row;

  const dispatch = createEventDispatcher<{
    clickCol: { event: Event; col: Column<T>; key: string };
    clickRow: { event: Event; row: T };
    clickCell: { event: Event; row: T; key: string };
    visibilityChanged: { visibleRows: T[] };
    multiSelectChanged: { event: Event; selected: ObjectOption[]; key: string };
  }>();

  let sortFunction: (row: T) => string | number = (_: T) => "";

  // Validation

  let showFilterHeader = columns.some((c) => {
    // check if there are any filter or search headers
    return c.filterOptions !== undefined || c.searchValue !== undefined;
  });
  let filterValues: Record<string, ObjectOption[]> = {};
  let columnByKey: Record<string, Column<T>>;
  $: {
    columnByKey = {};
    columns.forEach((col) => {
      columnByKey[col.key] = col;
    });
  }

  $: dispatch("visibilityChanged", { visibleRows });
  $: visibleRows = rows
    .filter((r) => {
      // get search and filter results/matches
      return Object.keys(filterSelections).every((f) => {
        const col = columnByKey[f];
        if (!col) return true;

        // check search (text input) matches
        if (col.search) {
          if (!col.searchValue) {
            throw new Error(`column ${col.key} is "search" but has no searchValue`);
          }

          // search is special: we can only have one thing selected.
          const sel = filterSelections[f]?.length && filterSelections[f][0];

          if (!sel) {
            // no input search terms => no filtering
            return true;
          }

          return col.searchValue.call(col, r).includes((sel.value ?? sel.label) + "");
        }

        // check filter (dropdown) matches

        const filterValue = col.filterValue ?? col.value;

        if (!filterValue) {
          throw new Error(`column ${col.key} filtered but lacks filterValue or value`);
        }

        if (!filterSelections[f]?.length) {
          return true;
        }

        const rowVal = filterValue.call(col, r);
        return filterSelections[f].some((x) => rowVal === (x.value ?? x.label));
      });
    })
    .map((r) =>
      Object.assign({}, r, {
        // internal row property for sort order
        $sortOn: sortFunction(r),
      })
    )
    .sort((a, b) => {
      if (!sortBy) return 0;
      else if (a.$sortOn > b.$sortOn) return sortOrder;
      else if (a.$sortOn < b.$sortOn) return -sortOrder;
      return 0;
    });

  const asStringArray = (v: any | any[]) =>
    []
      .concat(v)
      .filter((v) => typeof v === "string" && v !== "")
      .join(" ");

  const calculateFilterValues = () => {
    filterValues = {};
    columns.forEach((c) => {
      if (typeof c.filterOptions === "function") {
        filterValues[c.key] = c.filterOptions.call(c, rows);
      } else if (Array.isArray(c.filterOptions)) {
        filterValues[c.key] = [...c.filterOptions];
      } else {
        filterValues[c.key] = [];
      }
    });
  };

  $: if (sortBy) {
    let col = columnByKey[sortBy];
    if (col !== undefined && col.sortable === true && typeof col.value === "function") {
      const v = col.value;
      sortFunction = (r: T) => {
        if (col.numeric) {
          return v.call(col, r) || 0;
        }

        return v.call(col, r)?.toString() || "";
      };
    }
  }

  $: {
    // if filters are enabled, watch rows and columns
    if (showFilterHeader && columns && rows) {
      calculateFilterValues();
    }
  }

  const updateSortOrder = (colKey: string) => {
    return colKey === sortBy
      ? sortOrders[(sortOrders.findIndex((o) => o === sortOrder) + 1) % sortOrders.length]
      : sortOrders[0];
  };

  const handleClickCol = (event: Event, col: Column<T>) => {
    if (col.sortable) {
      sortOrder = updateSortOrder(col.key);
      sortBy = sortOrder ? col.key : undefined;
    }
    dispatch("clickCol", { event, col, key: col.key });
  };

  const handleClickRow = (event: Event, row: VisibleRow) => {
    dispatch("clickRow", { event, row });
  };

  const handleClickCell = (event: Event, row: VisibleRow, key: string) => {
    dispatch("clickCell", { event, row, key });
  };

  const handleMultiselectChange = (e: Event, col: Column<T>) => {
    dispatch("multiSelectChanged", { event: e, selected: filterSelections[col.key], key: col.key });
  };

  const clearAllFilters = () => {
    Object.keys(filterSelections).forEach((k) => {
      filterSelections[k] = [];
    });
  };

  $: showClearFilters = Object.values(filterSelections).some((a) => !!a && a.length > 0);
  $: hidden = hideWhenNoneVisible && visibleRows.length === 0;
  $: offset = $$slots["before-rows"] ? 2 : 0;

  export function getRows() {
    return visibleRows;
  }
</script>

<table class={asStringArray(classNameTable)} class:hidden>
  <colgroup>
    {#each columns as col}
      <col width={typeof col.width == "number" ? `${col.width}%` : col.width} />
    {/each}
  </colgroup>
  <thead class={asStringArray(classNameThead)}>
    {#if showFilterHeader}
      <tr class="table-sort-row">
        {#each columns as col, n}
          <th style="white-space: nowrap;">
            {#if filterValues[col.key]?.length}
              {#if col.multiFilter}
                <MultiSelect
                  --sms-padding="0 0px"
                  placeholder="select or type"
                  on:change={(e) => {
                    handleMultiselectChange(e, col);
                  }}
                  bind:selected={filterSelections[col.key]}
                  options={filterValues[col.key]}
                />
              {:else}
                <select
                  class={asStringArray(classNameSelect)}
                  on:change={(e) => {
                    if (e.target && e.target instanceof HTMLSelectElement) {
                      const val = parseInt(e.target.value);

                      filterSelections[col.key] = val === -1 ? [] : [filterValues[col.key][val]];
                    }
                  }}
                >
                  <!-- we have to update filterValues[col.key] with an array, and svelte's object
                  value binding doesn't seem to handle that well (it seemed to work until I selected
                  an option with value={[option]}, but then the label disappeared). so i'm just
                  using indices into the array. -->
                  <option value={-1} selected>All</option>
                  {#each filterValues[col.key] as option, i}
                    <option value={i}>{option.label}</option>
                  {/each}
                </select>
              {/if}
            {/if}

            {#if n === columns.length - 1 && showClearFilters}
              <div on:click={clearAllFilters} style="text-align: right; cursor: pointer;">
                clear filters ✕
              </div>
            {/if}
          </th>
        {/each}
      </tr>
    {/if}
    <slot name="header" {sortOrder} {sortBy}>
      <tr>
        {#each columns as col}
          <th
            on:click={(e) => handleClickCol(e, col)}
            class:isSortable={col.sortable}
            class:hasTooltip={col.tooltip}
            class={col.headerClass}
          >
            {@html col.title}
            <div class="th-controls">
              {#if sortBy === col.key}
                {@html sortOrder === 1 ? iconAsc : iconDesc}
              {:else if col.sortable}
                {@html iconSortable}
              {/if}
              {#if col.tooltip}
                <img src="static/assets/icons/info-circle.svg" alt="details" class="tooltip-icon" />
                <div class="tooltip">
                  {@html col.tooltip}
                </div>
              {/if}
            </div>
          </th>
        {/each}
      </tr>
    </slot>
  </thead>

  <tbody class={asStringArray(classNameTbody)}>
    {#if $$slots["before-rows"]}
      <tr class={asStringArray([classNameRow, "odd"])}>
        <slot name="before-rows" />
      </tr>
    {/if}
    {#each visibleRows as row, n (key(row))}
      <slot name="row" {row} {n}>
        <tr
          on:click={(e) => {
            handleClickRow(e, row);
          }}
          class={asStringArray([classNameRow, (n + offset) % 2 && "odd"])}
        >
          {#each columns as col}
            <td
              on:click={(e) => {
                handleClickCell(e, row, col.key);
              }}
              class={asStringArray([col.addClass && col.addClass(row), col.class, classNameCell])}
            >
              {#if col.renderComponent}
                {@const props = col.renderComponent.propsf?.call(col, row) || {
                  row,
                  ...(col.renderComponent.props || {}),
                }}
                <svelte:component this={col.renderComponent.component} {...props} />
              {:else if col.value}
                {col.value.call(col, row)}
              {/if}
            </td>
          {/each}
        </tr>
      </slot>
    {/each}
  </tbody>

  <!-- {#if footerRowsx && footerRowsx.length > 0}
    <tfoot>
      {#each footerRowsx as footerRow}
        <tr>
          {#each columns as col}
            <td
              on:click={(e) => {
                handleClickCell(e, footerRow, col.key);
              }}
              class={asStringArray([col.class, classNameCell])}
            >
              {#if col.renderComponent}
                <svelte:component
                  this={col.renderComponent.component || col.renderComponent}
                  {...col.renderComponent.props || {}}
                  row={footerRow}
                  {col}
                />
              {/if}
            </td>
          {/each}
        </tr>
      {/each}
    </tfoot>
  {/if} -->
</table>
{#if !visibleRows.length}
  <slot name="none-visible" />
{/if}

<style>
  table {
    width: 100%;
  }
  .isSortable {
    cursor: pointer;
  }

  .isClickable {
    cursor: pointer;
  }

  tr th select {
    width: 100%;
  }

  :global(div.multiselect) {
    margin: 3px 0px;
    background-color: white;
  }

  /* keep the filter multiselects relatively small to avoid making cols huge. */
  th :global(div.multiselect input) {
    max-width: 10ch;
  }

  th :global(div.multiselect > ul.options) {
    /* otherwise this is constrained by the max-width above and stuff gets truncated. */
    width: inherit;
    /* i can't get this to z-index above the 'clear all filters' :( */
  }

  .tooltip {
    display: block;
    visibility: hidden;
    position: absolute;
    z-index: 1;
    top: 100%;
    left: 50%;
    transform: translate(-50%, 20px);
    background-color: #3f3f3f;
    color: #fff;
    text-align: center;
    border-radius: 4px;
    padding: 4px 8px 6px;
    font-family: "REKKI-Regular", sans-serif;
    font-size: 13px;
    line-height: 20px;
    text-transform: none;
    white-space: nowrap;
    margin-top: 12px;
  }

  .tooltip:before {
    content: "";
    position: absolute;
    left: 50%;
    bottom: 100%;
    margin-left: -10px;
    margin-bottom: -1px;
    border-width: 9px 10px;
    border-style: solid;
    border-color: transparent transparent #3f3f3f transparent;
  }

  .tooltip-icon {
    display: inline-block;
  }

  .tooltip-icon:hover + .tooltip {
    visibility: visible;
    transition: all 0.15s;
    transform: translate(-50%, 0);
  }

  .th-controls {
    white-space: nowrap;
    display: none;
    gap: 3px;
    align-items: center;
    margin-top: 2px;
    margin-left: -1px;
  }

  .isSortable .th-controls,
  .hasTooltip .th-controls {
    display: inline-flex;
  }
</style>
